"use client";

import Image from "next/image";
import { useEffect, useState } from "react";

const STORAGE_KEY = "bannerModalShown";

type BannerModalProps = {
  image?: {
    url?: string;
    width?: number;
    height?: number;
  } | null;
  alt?: string;
};

export default function BannerModal({ image, alt }: BannerModalProps) {
  const [isOpen, setIsOpen] = useState(false);
  const imageUrl = image?.url;

  useEffect(() => {
    // No image set in the CMS — nothing to show.
    if (!imageUrl) return;

    // sessionStorage survives client-side navigation and reloads within the
    // same tab, but is cleared when the tab is closed. So the banner shows
    // when the homepage is freshly opened, but not when the visitor navigates
    // back to it while already browsing the site.
    // Wrapped in try/catch because some private-browsing modes throw on
    // storage access — in that case we just show the modal anyway.
    try {
      if (sessionStorage.getItem(STORAGE_KEY)) return;
      sessionStorage.setItem(STORAGE_KEY, "1");
    } catch {
      // storage unavailable — fall through and show the modal
    }

    setIsOpen(true);
  }, [imageUrl]);

  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") setIsOpen(false);
    };

    document.addEventListener("keydown", handleKeyDown);

    // Prevent the page behind the modal from scrolling.
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = originalOverflow;
    };
  }, [isOpen]);

  if (!isOpen || !imageUrl) return null;

  return (
    <div
      role='dialog'
      aria-modal='true'
      onClick={() => setIsOpen(false)}
      className='fixed inset-0 z-100 flex items-center -mt-20 justify-center bg-black/60 p-4 backdrop-blur-sm'
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className='relative w-full max-w-xl'
      >
        <button
          onClick={() => setIsOpen(false)}
          aria-label='Zavrieť'
          className='absolute top-2 right-2 z-10 cursor-pointer p-1 text-white drop-shadow-md transition-opacity hover:opacity-80'
        >
          <svg
            xmlns='http://www.w3.org/2000/svg'
            width='24'
            height='24'
            viewBox='0 0 24 24'
            fill='none'
            stroke='currentColor'
            strokeWidth='2'
            strokeLinecap='round'
            strokeLinejoin='round'
          >
            <line x1='18' y1='6' x2='6' y2='18' />
            <line x1='6' y1='6' x2='18' y2='18' />
          </svg>
        </button>

        <Image
          src={imageUrl}
          alt={alt || "Aktuálna ponuka"}
          width={image?.width ?? 1454}
          height={image?.height ?? 1454}
          priority
          className='h-auto w-full rounded-2xl shadow-2xl'
        />
      </div>
    </div>
  );
}
